import { useCallback, useMemo } from 'react';
import { nanoid } from 'nanoid';
import { TaskModel } from '../../../mobx/models/task';
import { rootStore } from '../../../mobx/rootStore';
import { getRawComment } from '../../../helpers/common';
import { createNewComment, updateRecurringTaskDetails, updateTaskDetails } from '../../../services/taskServices';
import { COMMENT_TYPE } from '../../../global/constants';

export interface AssignActions {
  onAssign: (userId: string) => void | Promise<void>;
  onUnassign: () => void | Promise<void>;
}

export interface TaskAssignOptions {
  isRecurring?: boolean;
  isProjectTemplate?: boolean;
}

export const useTaskAssignActions = (
  currentTask: TaskModel | null | undefined,
  options: TaskAssignOptions = {}
): AssignActions => {
  const { isRecurring = false, isProjectTemplate = false } = options;

  const onAssign = useCallback(
    async (userId: string) => {
      const currentUser = rootStore.currentUser;
      if (!currentUser || !currentTask) return;

      const isAlreadyAssigned = currentTask.hasAssignee(userId);
      const commentType = isAlreadyAssigned ? COMMENT_TYPE.REMOVE_ASSIGNEE : COMMENT_TYPE.ADD_ASSIGNEE;

      if (isProjectTemplate) {
        const currentProjectTemplate = rootStore.projectTemplateStore.currentProjectTemplate;
        const updatedTaskList = currentProjectTemplate?.tasks.map((item) => {
          if (item?.id === currentTask.id) {
            if (isAlreadyAssigned) item.removeAssignee(userId);
            else item.addAssignee(userId, true);
          }
          return item;
        });
        currentProjectTemplate?.setTasks(updatedTaskList || []);
        return;
      }

      if (isAlreadyAssigned) currentTask.removeAssignee(userId);
      else currentTask.addAssignee(userId, false);

      const newComment = { id: nanoid(), type: commentType, assigneeId: userId };
      currentTask.upsertComment(getRawComment(newComment, currentUser));

      const payload = { taskUsers: currentTask.usersIds, projectId: currentTask.projectId };
      if (isRecurring) {
        await updateRecurringTaskDetails(currentTask.id, payload);
      } else {
        await updateTaskDetails(currentTask.id, payload);
        await createNewComment(currentTask.id, { Type: commentType, Assignee: userId });
      }
    },
    [currentTask, isRecurring, isProjectTemplate]
  );

  const onUnassign = useCallback(async () => {
    const currentUser = rootStore.currentUser;
    if (!currentUser || !currentTask) return;

    if (isProjectTemplate) {
      const currentProjectTemplate = rootStore.projectTemplateStore.currentProjectTemplate;
      const updatedTaskList = currentProjectTemplate?.tasks.map((item) => {
        if (item?.id === currentTask.id) item.clearAssignees();
        return item;
      });
      currentProjectTemplate?.setTasks(updatedTaskList || []);
      return;
    }

    currentTask.clearAssignees();
    const payload = { taskUsers: currentTask.usersIds, projectId: currentTask.projectId };
    if (isRecurring) {
      await updateRecurringTaskDetails(currentTask.id, payload);
    } else {
      currentTask.upsertComment(getRawComment({ id: nanoid(), type: COMMENT_TYPE.UNASSIGN }, currentUser));
      await updateTaskDetails(currentTask.id, payload);
      await createNewComment(currentTask.id, { Type: COMMENT_TYPE.UNASSIGN });
    }
  }, [currentTask, isRecurring, isProjectTemplate]);

  return useMemo(() => ({ onAssign, onUnassign }), [onAssign, onUnassign]);
};

export const useCreateModalAssignActions = (): AssignActions => {
  const onAssign = useCallback((userId: string) => {
    const createTaskInput = rootStore.taskStore.createTaskInput;
    if (createTaskInput.hasAssignee(userId)) createTaskInput.removeAssignee(userId);
    else createTaskInput.addAssignee(userId);
  }, []);

  const onUnassign = useCallback(() => {
    rootStore.taskStore.createTaskInput.clearAssignees();
  }, []);

  return useMemo(() => ({ onAssign, onUnassign }), [onAssign, onUnassign]);
};

export const useTaskTemplateAssignActions = (): AssignActions => {
  const onAssign = useCallback((userId: string) => {
    const template = rootStore.projectTemplateStore.currentTaskTemplate;
    if (!template) return;
    if (template.hasAssignee(userId)) template.removeAssignee(userId);
    else template.addAssignee(userId);
  }, []);

  const onUnassign = useCallback(() => {
    rootStore.projectTemplateStore.currentTaskTemplate?.clearAssignees();
  }, []);

  return useMemo(() => ({ onAssign, onUnassign }), [onAssign, onUnassign]);
};

export const useViewFilterAssignActions = (): AssignActions => {
  const onAssign = useCallback((userId: string) => {
    const createViewInput = rootStore.viewStore.createViewInput;
    const current = createViewInput?.filters?.assign?.assignees || [];
    const next = current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId];
    createViewInput.setAssignFilter(next, createViewInput.filters.assign.type);
  }, []);

  const onUnassign = useCallback(() => {
    const createViewInput = rootStore.viewStore.createViewInput;
    const current = createViewInput?.filters?.assign?.assignees || [];
    const next = current.includes('unassigned')
      ? current.filter((id) => id !== 'unassigned')
      : [...current, 'unassigned'];
    createViewInput.setAssignFilter(next, createViewInput.filters.assign.type);
  }, []);

  return useMemo(() => ({ onAssign, onUnassign }), [onAssign, onUnassign]);
};
